import { useState } from 'preact/hooks'
import { addDays, dayOfWeek } from '~/calculations/calendar'
import { Button } from '~/components/primitives/Button'
import { FITNESS_DOMAIN_LABELS } from '~/models/fitnessDomain'
import type { MaterialisedSession, TrainingWeekRecord } from '~/models/trainingWeek'
import { isWithinWeek, openSessionsBefore, sessionsOn } from '~/models/trainingWeek'
import { WEEKDAY_LABELS, type Weekday } from '~/models/weekday'
import {
  completeTrainingSession,
  moveTrainingSession,
  skipTrainingSession,
} from '~/services/trainingWeekService'
import { useDatabase } from '~/storage/DatabaseContext'
import { type CalendarDate, calendarDateFrom, unwrapDate } from '~/types/dates'
import styles from './TodayPage.module.css'

/**
 * Today's training, and the three things that can be done to a session.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  DONE / SKIP IS A PRESSED GROUP, AND MOVE IS SECONDARY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Neither counts against D-104 §9's one-primary-action rule, and the reason is
 *  the one `ui-ux-spec.md` already gives: *"a control reporting a pressed state
 *  is answering a question rather than offering an action"*. A session outcome
 *  is an answer about what happened and it is reversible while the week is open,
 *  exactly like the daily check's three answers (D-106 §10).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  EVERY CONTROL IS NAMED WITH ITS SESSION AND ITS DAY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  *"Skip Wednesday's Pull day"*, never *"Skip"*. Three sessions in a week
 *  otherwise produce three identically named buttons — and Playwright matches an
 *  accessible name by substring, which is the collision D-105 predicted for
 *  exactly these names (D-106 §10).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NO RED, NO "OVERDUE", NO COUNT OF MISSES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  An unresolved session from earlier in the week appears under *"Still open
 *  from this week"* with its weekday and nothing else — no warning colour, no
 *  elapsed time, no accumulation across weeks (D-105 §5). It is a state, not a
 *  judgement, and at the week boundary it simply stops being actionable.
 */

interface SessionListProps {
  week: TrainingWeekRecord
  now: Date
  onChanged: () => void
}

function weekdayOf(date: CalendarDate): Weekday {
  return dayOfWeek(date) as Weekday
}

/** How a resolved session reads once it is answered (D-106 §5). */
function outcomeLabel(session: MaterialisedSession): string | undefined {
  if (session.outcome === undefined) return undefined
  return session.outcome.kind === 'completed' ? 'Done' : 'Skipped'
}

export function SessionList({ week, now, onChanged }: SessionListProps) {
  const today = calendarDateFrom(now)
  const todays = sessionsOn(week, today)
  const stillOpen = openSessionsBefore(week, today)

  if (todays.length === 0 && stillOpen.length === 0) return null

  return (
    <section class={styles.sessions} aria-labelledby="today-sessions">
      <h2 id="today-sessions" class={styles.sessionsHeading}>
        Training
      </h2>

      {todays.length === 0 ? (
        <p class={styles.sessionsNote}>Nothing scheduled today.</p>
      ) : (
        <ul class={styles.sessionList}>
          {todays.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              week={week}
              today={today}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}

      {stillOpen.length === 0 ? null : (
        <>
          {/* Daily closure never implies weekly closure (D-106 §4). */}
          <h3 class={styles.sessionsHeading}>Still open from this week</h3>
          <ul class={styles.sessionList}>
            {stillOpen.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                week={week}
                today={today}
                onChanged={onChanged}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function SessionRow({
  session,
  week,
  today,
  onChanged,
}: {
  session: MaterialisedSession
  week: TrainingWeekRecord
  today: CalendarDate
  onChanged: () => void
}) {
  const db = useDatabase()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  /* The day this session stands on, which is what its controls are named after.
     A session moved to Thursday is "Thursday's", not "Wednesday's". */
  const day = WEEKDAY_LABELS[weekdayOf(session.scheduledDate)]
  const named = `${day}’s ${session.name}`
  const resolved = outcomeLabel(session)
  const tomorrow = addDays(today, 1)
  const canMove = session.outcome === undefined && isWithinWeek(week, tomorrow)

  const run = async (action: Promise<{ ok: boolean; error?: { message: string } }>) => {
    setBusy(true)
    setError(undefined)
    const result = await action
    setBusy(false)
    if (!result.ok) {
      setError(result.error?.message ?? 'That could not be recorded.')
      return
    }
    onChanged()
  }

  return (
    <li class={styles.sessionRow}>
      <div class={styles.sessionText}>
        <p class={styles.sessionName}>
          {session.name}
          {session.origin === 'oneOff' ? <span class={styles.sessionTag}> · Added</span> : null}
        </p>
        <p class={styles.sessionMeta}>
          {FITNESS_DOMAIN_LABELS[session.domain]}
          {session.expectedMinutes === undefined ? '' : ` · ${session.expectedMinutes} min`}
          {unwrapDate(session.scheduledDate) === unwrapDate(today) ? '' : ` · ${day}`}
          {/* Never a colour alone: the word is the state (D-106 §10). */}
          {resolved === undefined ? '' : ` · ${resolved}`}
        </p>
      </div>

      <div class={styles.sessionActions}>
        <Button
          variant="secondary"
          busy={busy}
          pressed={session.outcome?.kind === 'completed'}
          label={`Done: ${named}`}
          onClick={() => void run(completeTrainingSession(db, session.id, new Date()))}
        >
          Done
        </Button>
        <Button
          variant="secondary"
          busy={busy}
          pressed={session.outcome?.kind === 'skipped'}
          label={`Skip ${named}`}
          onClick={() => void run(skipTrainingSession(db, session.id, new Date()))}
        >
          Skip
        </Button>
        {canMove ? (
          <Button
            variant="secondary"
            busy={busy}
            label={`Move ${named} to the next day`}
            onClick={() => void run(moveTrainingSession(db, session.id, tomorrow, new Date()))}
          >
            Move a day
          </Button>
        ) : null}
      </div>

      {error === undefined ? null : (
        <p class={styles.sessionError} role="alert">
          {error}
        </p>
      )}
    </li>
  )
}
